import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import axios from 'axios';
import {
    Bars3Icon,
    SignalIcon,
    SignalSlashIcon,
    ArrowRightOnRectangleIcon,
    PlayCircleIcon,
} from "@heroicons/react/24/outline";

const FLEET_POLL_MS = 30000;

/**
 * Chip de statut du parc dans le header (style console signage pro) :
 * - vert  : N écrans en diffusion (clic -> /nowplaying)
 * - ambre : personne ne diffuse mais l'API répond
 * - rouge : API injoignable
 * Poll léger (30 s) sur /now_playing — la page Now Playing garde son propre poll rapide.
 */
function FleetChip() {
    const [state, setState] = useState({ status: 'loading', playing: 0, offline: 0 });

    useEffect(() => {
        let alive = true;
        const poll = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/now_playing`, { timeout: 8000 });
                if (!alive) return;
                const counts = res.data?.counts || {};
                setState({ status: 'ok', playing: counts.playing || 0, offline: counts.offline || 0 });
            } catch {
                if (alive) setState({ status: 'down', playing: 0, offline: 0 });
            }
        };
        poll();
        const id = setInterval(poll, FLEET_POLL_MS);
        return () => { alive = false; clearInterval(id); };
    }, []);

    if (state.status === 'loading') return null;

    if (state.status === 'down') {
        return (
            <span className="hidden items-center gap-1.5 rounded-full bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 ring-1 ring-red-200 md:inline-flex">
                <SignalSlashIcon className="h-4 w-4" />
                API hors ligne
            </span>
        );
    }

    const playing = state.playing > 0;
    return (
        <Link
            to="/nowplaying"
            title="Voir le détail de la diffusion"
            className={`hidden items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ring-1 transition md:inline-flex ${
                playing
                    ? "bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100"
                    : "bg-amber-50 text-amber-700 ring-amber-200 hover:bg-amber-100"
            }`}
        >
            <span className="relative flex h-2 w-2">
                {playing && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />}
                <span className={`relative inline-flex h-2 w-2 rounded-full ${playing ? "bg-emerald-500" : "bg-amber-500"}`} />
            </span>
            {playing ? `${state.playing} écran${state.playing > 1 ? "s" : ""} en diffusion` : "Aucune diffusion"}
            <PlayCircleIcon className="h-4 w-4 opacity-70" />
        </Link>
    );
}

function Header({ onToggleSidebar }) {
    const { keycloak } = useKeycloak();

    const handleLogout = () => keycloak.logout();

    const given = keycloak?.tokenParsed?.given_name || "";
    const family = keycloak?.tokenParsed?.family_name || "";
    const fullName = `${given} ${family}`.trim();
    const initials = `${given.charAt(0)}${family.charAt(0)}`.toUpperCase() || "?";

    return (
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/80 px-4 backdrop-blur-md lg:px-6">
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={onToggleSidebar}
                    aria-label="Ouvrir le menu latéral"
                    className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 lg:hidden"
                >
                    <Bars3Icon className="h-5 w-5" />
                </button>
                <Link to="/home" className="flex items-center gap-2">
                    <div className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 shadow-md shadow-indigo-500/30">
                        <SignalIcon className="h-5 w-5 text-white" />
                    </div>
                    <span className="text-lg font-bold tracking-tight text-slate-800">
                        GRAPH<span className="text-indigo-500">COM</span>
                    </span>
                    <span className="ml-1 hidden rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 lg:inline">
                        Signage Cloud
                    </span>
                </Link>
            </div>

            <div className="flex items-center gap-3">
                <FleetChip />
                {keycloak.authenticated && fullName && (
                    <div className="hidden items-center gap-2 rounded-full bg-slate-100 py-1 pl-1 pr-3 sm:flex">
                        <span className="grid h-7 w-7 place-items-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-xs font-semibold text-white">
                            {initials}
                        </span>
                        <span className="text-sm font-medium text-slate-700">{fullName}</span>
                    </div>
                )}
                <button
                    type="button"
                    onClick={handleLogout}
                    className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-900"
                >
                    <ArrowRightOnRectangleIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">Déconnexion</span>
                </button>
            </div>
        </header>
    );
}

export default Header;
